import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { PullProjectService } from '@service/pull-project.service';
import { VersionsHistoryService } from '@service/versions-history.service';
import { Shipyard } from 'icreate/common/models/shipyard';
import { PublishedVersion } from 'icreate/common/models/versions-history';
import { Vessel } from 'icreate/common/models/vessel';

@Component({
    selector: 'app-new-vessel-template-option',
    templateUrl: './new-vessel-template-option.component.html',
    styleUrl: './new-vessel-template-option.component.css',
    standalone: false,
})
export class NewVesselTemplateOptionComponent {
    @Input() shipyardList: Shipyard[];
    @Output() onTemplateSelected: EventEmitter<Vessel> = new EventEmitter();
    @Output() onReferenceSelected: EventEmitter<{
        shipyardiApi: string;
        vesseliApi: string;
        version: string;
        vesselName: string;
        shipyardName: string;
    }> = new EventEmitter();
    @Output() onLoadingVessel: EventEmitter<boolean> = new EventEmitter();
    @Output() onCloseDialog: EventEmitter<void> = new EventEmitter();
    @ViewChild('infoContainer') infoContainer: ElementRef;
    @ViewChild('templateListContainer') templateListContainer: ElementRef;
    @ViewChild('listInput') listInput: ElementRef;

    constructor(
        private versionHistory: VersionsHistoryService,
        private router: Router,
        private pullService: PullProjectService
    ) {}

    showInfoContainer: boolean = false;
    loadingVersions: boolean = false;
    emptyVersionList: boolean = false;

    filteredOptionsObject: VesselOption[];
    optionsObject: VesselOption[];
    selectedOption: VesselOption;
    selectedVessel: Vessel;

    versionsList: PublishedVersion[];
    compareVersion: string;

    inputValue: string;

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (this.showInfoContainer && !this.infoContainer?.nativeElement.contains(event.target)) {
            this.showInfoContainer = false;
        }
    }

    ngOnInit() {
        this.prepareTemplateList();
    }

    prepareTemplateList() {
        if (!this.shipyardList || this.shipyardList.length == 0) return;
        this.optionsObject = [];
        this.shipyardList.map(shipyard => {
            if (shipyard.vessels.length == 0) return;
            const vessels: VesselOption[] = shipyard.vessels.map<VesselOption>(vessel => {
                return {
                    vesselName: `${vessel.number} - ${vessel.name}`,
                    vesselNumber: vessel.number,
                    shipyardName: shipyard.name,
                    iApiCompanyId: shipyard.iApiCompanyId,
                    iAPIVesselId: vessel.iAPIVesselId,
                    extraListInfo: shipyard.name,
                };
            });
            this.optionsObject.push(...vessels);
        });
    }

    async optionSelected(item: VesselOption) {
        this.emptyVersionList = false;
        this.selectedVessel = null;
        this.selectedOption = item;
        this.compareVersion = null;
        const selectedShipyard = this.shipyardList.find(
            shipyard => this.selectedOption.iApiCompanyId === shipyard.iApiCompanyId
        );
        this.selectedVessel = selectedShipyard.vessels.find(vessel => vessel.number === item.vesselNumber);
        this.onTemplateSelected.emit(null);
        this.onReferenceSelected.emit(null);
        await this.getVersionList();
    }

    async getVersionList() {
        try {
            this.loadingVersions = true;
            this.versionsList = await this.versionHistory.getVersionsList(
                this.selectedOption.iApiCompanyId,
                this.selectedOption.iAPIVesselId
            );
            if (this.versionsList.length == 0) {
                this.emptyVersionList = true;
                this.loadingVersions = false;
                return;
            }
            this.compareVersion = this.versionsList[0].name;
            const pulledVessel = await this.pullService.pullVesselByVersion(
                this.selectedOption.iApiCompanyId,
                this.selectedOption.iAPIVesselId,
                this.compareVersion
            );
            this.loadingVersions = false;
            if (pulledVessel) {
                this.onTemplateSelected.emit(pulledVessel);
                this.onReferenceSelected.emit({
                    shipyardiApi: this.selectedOption.iApiCompanyId,
                    vesseliApi: this.selectedOption.iAPIVesselId,
                    version: this.compareVersion,
                    vesselName: this.selectedOption.vesselName,
                    shipyardName: this.selectedOption.shipyardName,
                });
            }
        } catch (err) {
            console.error(err);
        }
    }

    async referenceSelected(version) {
        this.onLoadingVessel.emit(true);
        this.compareVersion = version;
        const pulledVessel = await this.pullService.pullVesselByVersion(
            this.selectedOption.iApiCompanyId,
            this.selectedOption.iAPIVesselId,
            this.compareVersion
        );
        this.loadingVersions = false;
        this.onLoadingVessel.emit(false);
        if (pulledVessel) {
            this.onTemplateSelected.emit(pulledVessel);
            this.onReferenceSelected.emit({
                shipyardiApi: this.selectedOption.iApiCompanyId,
                vesseliApi: this.selectedOption.iAPIVesselId,
                version: this.compareVersion,
                vesselName: this.selectedOption.vesselName,
                shipyardName: this.selectedOption.shipyardName,
            });
        }
    }

    openVersionHistory() {
        const vessel = this.selectedVessel;
        this.onCloseDialog.emit();
        this.router.navigateByUrl(`${vessel.shipyardId}/${vessel.id}/history`, {
            state: { origin: 'new-vessel-home' },
        });
    }

    getVesselName(vessel: VesselOption) {
        return vessel.vesselName + ' - ' + vessel.vesselNumber;
    }
}

interface VesselOption {
    shipyardName: string;
    vesselName: string;
    vesselNumber: string;
    iApiCompanyId: string;
    iAPIVesselId: string;
}
